import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTournament } from "@/lib/TournamentContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, AtSign, MessageSquare, Trophy, Shield, Clock, BellRing, BellOff, UserPlus, UserCheck, GitBranch } from "lucide-react";
import { pushSupported, isPushEnabled, enablePush, disablePush } from "@/lib/push";

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

const CHAT_TYPES = new Set(["mention", "reply"]);

const iconFor = (t) => {
  if (t === "mention") return <AtSign size={14} />;
  if (t === "reply") return <MessageSquare size={14} />;
  if (t === "result") return <Trophy size={14} />;
  if (t === "survivor_result") return <Shield size={14} />;
  if (t === "group_join_request") return <UserPlus size={14} />;
  if (t === "group_join_approved") return <UserCheck size={14} />;
  if (t === "bracket_update") return <GitBranch size={14} />;
  return <Clock size={14} />;
};

export default function NotificationBell() {
  const { active } = useTournament();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const ref = useRef(null);
  const tid = active?.tournament_id;

  const loadCount = useCallback(async () => {
    if (!tid) return;
    try {
      const { data } = await api.get(`/tournaments/${tid}/notifications/count`);
      setCount(data.unread);
    } catch { /* ignore */ }
  }, [tid]);

  useEffect(() => {
    loadCount();
    const i = setInterval(loadCount, 30000);
    return () => clearInterval(i);
  }, [loadCount]);

  useEffect(() => { isPushEnabled().then(setPushOn); }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && tid) {
      try {
        const { data } = await api.get(`/tournaments/${tid}/notifications`);
        setItems(data);
        if (count > 0) {
          await api.post(`/tournaments/${tid}/notifications/read-all`);
          setCount(0);
        }
      } catch { /* keep dropdown open with whatever we have */ }
    }
  };

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushOn) {
        await disablePush();
        setPushOn(false);
        toast.success("Deadline reminders turned off");
      } else {
        await enablePush();
        setPushOn(true);
        toast.success("Reminders on — we'll nudge you before deadlines");
      }
    } catch (e) {
      toast.error(e?.message || "Couldn't update reminders");
    } finally { setPushBusy(false); }
  };

  const openItem = async (n) => {
    if (!n.read) { try { await api.post(`/notifications/${n.notification_id}/read`); } catch { /* ignore */ } }
    setOpen(false);
    if (CHAT_TYPES.has(n.type)) {
      navigate(`/chat?scope=${n.scope}&room_id=${encodeURIComponent(n.room_id)}`);
    } else {
      navigate(n.link || "/dashboard");
    }
  };

  if (!tid) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell"
        onClick={toggle}
        className="w-8 h-8 surface-card hover:border-primary-accent flex items-center justify-center relative"
        title="Notifications"
      >
        <Bell size={14} />
        {count > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 bg-primary-accent text-white text-[10px] font-display flex items-center justify-center rounded-full"
          >
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 max-w-[90vw] surface-card z-50" data-testid="notification-dropdown">
          <div className="px-4 py-2.5 border-b border-white/10 flex items-center justify-between gap-2">
            <span className="font-display tracking-widest text-sm uppercase">Notifications</span>
            {pushSupported() && (
              <button
                data-testid="push-toggle"
                onClick={togglePush}
                disabled={pushBusy}
                title={pushOn ? "Turn off deadline reminders" : "Get push reminders before deadlines"}
                className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors disabled:opacity-50 ${
                  pushOn ? "border-green-500/50 text-green-400" : "border-zinc-700 text-zinc-400 hover:border-primary-accent hover:text-primary-accent"
                }`}
              >
                {pushOn ? <BellRing size={11} /> : <BellOff size={11} />}
                {pushBusy ? "…" : pushOn ? "Reminders On" : "Reminders Off"}
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 && (
              <div className="px-4 py-8 text-center text-zinc-500 text-sm" data-testid="notification-empty">
                You&apos;re all caught up.
              </div>
            )}
            {items.map((n) => {
              const isChat = CHAT_TYPES.has(n.type);
              return (
                <button
                  key={n.notification_id}
                  data-testid={`notification-${n.notification_id}`}
                  onClick={() => openItem(n)}
                  className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 flex gap-2.5 ${n.read ? "opacity-60" : ""}`}
                >
                  <span className={`pt-0.5 ${n.type === "result" ? "text-yellow-400" : "text-primary-accent"}`}>
                    {iconFor(n.type)}
                  </span>
                  <div className="min-w-0 flex-1">
                    {isChat ? (
                      <>
                        <div className="text-sm text-zinc-200">
                          <span className="font-display tracking-wide">{n.actor_name}</span>
                          <span className="text-zinc-500"> {n.type === "mention" ? "mentioned you" : "replied to you"} · {timeAgo(n.created_at)}</span>
                        </div>
                        <div className="text-xs text-zinc-500 truncate mt-0.5">{n.excerpt}</div>
                      </>
                    ) : (
                      <>
                        <div className="text-sm text-zinc-200 flex items-center justify-between gap-2">
                          <span className="font-display tracking-wide truncate">{n.title}</span>
                          <span className="text-zinc-500 text-xs flex-shrink-0">{timeAgo(n.created_at)}</span>
                        </div>
                        <div className="text-xs text-zinc-500 mt-0.5">{n.body}</div>
                      </>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
